import { supabase } from './supabase';
import type { DailyTracking } from '../types';

const DEFAULT_VALUES: Partial<DailyTracking> = {
  sleepHours: 0,
  sleepQuality: 0,
  activityLevel: 0,
  energyLevel: 0,
  stressLevel: 0,
  gurgling: 0,
  bloating: 0,
  stomachPain: 0,
  gasLevel: 0,
  heartburnLevel: 0,
  nauseaLevel: 0,
  vomitingLevel: 0,
  stomachPainLevel: 0,
  foodChange: 0,
  foodHeaviness: 0,
  alcoholLevel: 0,
  vomiting: false,
  nausea: false,
  heartburn: false,
  stomachPainBool: false,
  stressReason: '',
  otherStomachSymptoms: '',
  foodChangeNote: null,
  foodHeavinessNote: null,
  alcoholNote: null,
  customNote: '',
  notes: ''
};

/** Tekst -> null gdy puste (trim) */
const toTextOrNull = (v: string | undefined | null): string | null => {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
};

function isIsoDate(date: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(date);
}

const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

const toDbFormat = (data: Partial<DailyTracking>) => {
  const result: Record<string, unknown> = {
    id: data.id,
    user_id: data.userId,
    date: data.date,
    updated_at: new Date().toISOString()
  };

  if (data.energyLevel !== undefined) result.energy_level = clamp(Number(data.energyLevel), 0, 5);
  if (data.energyTrend !== undefined && ['up', 'down', 'stable'].includes(data.energyTrend)) {
    result.energy_trend = data.energyTrend;
  }
  if (data.stressLevel !== undefined) result.stress_level = clamp(Number(data.stressLevel), 0, 5);
  if (data.stressTrend !== undefined && ['up', 'down', 'stable'].includes(data.stressTrend)) {
    result.stress_trend = data.stressTrend;
  }
  if (data.stressReason !== undefined) result.stress_reason = toTextOrNull(String(data.stressReason ?? ''));

  if (data.heartburnLevel !== undefined) {
    const v = clamp(Number(data.heartburnLevel), 0, 5);
    result.heartburn_level = v;
    result.heartburn = v > 0;
  }
  if (data.nauseaLevel !== undefined) {
    const v = clamp(Number(data.nauseaLevel), 0, 5);
    result.nausea_level = v;
    result.nausea = v > 0;
  }
  if (data.vomitingLevel !== undefined) {
    const v = clamp(Number(data.vomitingLevel), 0, 5);
    result.vomiting_level = v;
    result.vomiting = v > 0;
  }
  if (data.stomachPainLevel !== undefined) {
    const v = clamp(Number(data.stomachPainLevel), 0, 5);
    result.stomach_pain_level = v;
    result.stomach_pain_bool = v > 0;
  }
  if (data.stomachPain !== undefined) result.stomach_pain = clamp(Number(data.stomachPain), 0, 5);
  if (data.bloating !== undefined) result.bloating = clamp(Number(data.bloating), 0, 5);
  if (data.sleepHours !== undefined) result.sleep_hours = clamp(Number(data.sleepHours), 0, 15);
  if (data.sleepQuality !== undefined) result.sleep_quality = clamp(Number(data.sleepQuality), 0, 5);
  if (data.activityLevel !== undefined) result.activity_level = clamp(Number(data.activityLevel), 0, 5);
  if (data.gurgling !== undefined) result.gurgling = clamp(Number(data.gurgling), 0, 5);
  if (data.gasLevel !== undefined) result.gas_level = clamp(Number(data.gasLevel), 0, 5);
  if (data.foodChange !== undefined) result.food_change = clamp(Number(data.foodChange), 0, 5);
  if (data.foodHeaviness !== undefined) result.food_heaviness = clamp(Number(data.foodHeaviness), 0, 5);
  if (data.alcoholLevel !== undefined) result.alcohol_level = clamp(Number(data.alcoholLevel), 0, 5);

  if (data.otherStomachSymptoms !== undefined) result.other_stomach_symptoms = toTextOrNull(String(data.otherStomachSymptoms ?? ''));
  if (data.foodChangeNote !== undefined) result.food_change_note = toTextOrNull(String(data.foodChangeNote ?? ''));
  if (data.foodHeavinessNote !== undefined) result.food_heaviness_note = toTextOrNull(String(data.foodHeavinessNote ?? ''));
  if (data.alcoholNote !== undefined) result.alcohol_note = toTextOrNull(String(data.alcoholNote ?? ''));
  const notesVal = data.notes !== undefined ? toTextOrNull(String(data.notes ?? '')) : (data.customNote !== undefined ? toTextOrNull(String(data.customNote ?? '')) : undefined);
  if (notesVal !== undefined) {
    result.notes = notesVal;
    result.custom_note = notesVal;
  }

  Object.keys(result).forEach(key => {
    if (result[key] === undefined || result[key] === null) delete result[key];
  });
  return result;
};

const fromDbFormat = (data: Record<string, unknown>): DailyTracking => ({
  id: String(data.id),
  userId: String(data.user_id),
  date: String(data.date),
  energyLevel: Number(data.energy_level) || 0,
  energyTrend: (data.energy_trend as 'up' | 'down' | 'stable') || undefined,
  stressLevel: Number(data.stress_level) || 0,
  stressTrend: (data.stress_trend as 'up' | 'down' | 'stable') || undefined,
  stressReason: data.stress_reason != null ? String(data.stress_reason) : undefined,
  sleepHours: Number(data.sleep_hours) || 0,
  sleepQuality: Number(data.sleep_quality) || 0,
  activityLevel: Number(data.activity_level) || 0,
  gurgling: Number(data.gurgling) || 0,
  bloating: Number(data.bloating) || 0,
  stomachPain: Number(data.stomach_pain) || 0,
  gasLevel: Number(data.gas_level) || 0,
  heartburnLevel: data.heartburn_level != null ? Number(data.heartburn_level) : (data.heartburn ? 1 : 0),
  nauseaLevel: data.nausea_level != null ? Number(data.nausea_level) : (data.nausea ? 1 : 0),
  vomitingLevel: data.vomiting_level != null ? Number(data.vomiting_level) : (data.vomiting ? 1 : 0),
  stomachPainLevel: data.stomach_pain_level != null ? Number(data.stomach_pain_level) : (data.stomach_pain_bool ? 1 : 0),
  vomiting: Boolean(data.vomiting),
  nausea: Boolean(data.nausea),
  heartburn: Boolean(data.heartburn),
  stomachPainBool: Boolean(data.stomach_pain_bool),
  otherStomachSymptoms: data.other_stomach_symptoms ? String(data.other_stomach_symptoms) : undefined,
  foodChange: Number(data.food_change) || 0,
  foodHeaviness: Number(data.food_heaviness) || 0,
  alcoholLevel: Number(data.alcohol_level) || 0,
  foodChangeNote: data.food_change_note != null ? String(data.food_change_note) : null,
  foodHeavinessNote: data.food_heaviness_note != null ? String(data.food_heaviness_note) : null,
  alcoholNote: data.alcohol_note != null ? String(data.alcohol_note) : null,
  customNote: data.custom_note != null ? String(data.custom_note) : undefined,
  notes: data.notes != null ? String(data.notes) : undefined,
  createdAt: String(data.created_at),
  updatedAt: String(data.updated_at)
});

/** Zwraca wartości domyślne (0) dla brakujących pól */
const withDefaults = (t: DailyTracking | null, date: string, userId: string): DailyTracking => {
  if (!t) {
    return {
      id: `tracking-${userId}-${date}`,
      userId,
      date,
      ...DEFAULT_VALUES,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    } as DailyTracking;
  }
  return {
    ...t,
    sleepHours: t.sleepHours ?? 0,
    sleepQuality: t.sleepQuality ?? 0,
    activityLevel: t.activityLevel ?? 0,
    energyLevel: t.energyLevel ?? 0,
    stressLevel: t.stressLevel ?? 0,
    gurgling: t.gurgling ?? 0,
    bloating: t.bloating ?? 0,
    stomachPain: t.stomachPain ?? 0,
    gasLevel: t.gasLevel ?? 0,
    heartburnLevel: t.heartburnLevel ?? 0,
    nauseaLevel: t.nauseaLevel ?? 0,
    vomitingLevel: t.vomitingLevel ?? 0,
    stomachPainLevel: t.stomachPainLevel ?? 0,
    foodChange: t.foodChange ?? 0,
    foodHeaviness: t.foodHeaviness ?? 0,
    alcoholLevel: t.alcoholLevel ?? 0,
    vomiting: t.vomiting ?? false,
    nausea: t.nausea ?? false,
    heartburn: t.heartburn ?? false,
    stomachPainBool: t.stomachPainBool ?? false
  };
};

export const dailyTrackingService = {
  async getByDate(_userId: string, date: string): Promise<DailyTracking | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Użytkownik nie jest zalogowany');
    if (!date || !isIsoDate(date)) throw new Error('Nieprawidłowa data (wymagany format YYYY-MM-DD)');

    const { data, error } = await supabase
      .from('daily_tracking')
      .select('*')
      .eq('user_id', user.id)
      .eq('date', date)
      .maybeSingle();

    if (error) throw error;
    return data ? fromDbFormat(data) : null;
  },

  /** Pobiera rekord lub tworzy z domyślnymi wartościami (upsert 0) – dla wejścia w widok dnia */
  async getOrCreateForDate(userId: string, date: string): Promise<DailyTracking> {
    const existing = await this.getByDate(userId, date);
    if (existing) return withDefaults(existing, date, userId);

    const defaults: Partial<DailyTracking> = {
      ...DEFAULT_VALUES,
      date,
      userId
    };
    return this.upsert(defaults);
  },

  async upsert(tracking: Partial<DailyTracking>): Promise<DailyTracking> {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('Użytkownik nie jest zalogowany');
    if (!tracking.date || !isIsoDate(tracking.date)) {
      throw new Error('Nieprawidłowa data (wymagany format YYYY-MM-DD)');
    }

    const deterministicId = `tracking-${user.id}-${tracking.date}`;

    const { data: existing } = await supabase
      .from('daily_tracking')
      .select('id')
      .eq('user_id', user.id)
      .eq('date', tracking.date)
      .maybeSingle();

    const dbData: Record<string, unknown> = {
      ...toDbFormat(tracking),
      user_id: user.id,
      id: existing?.id || tracking.id || deterministicId
    };

    Object.keys(dbData).forEach(key => {
      if (dbData[key] === undefined || dbData[key] === null) delete dbData[key];
    });

    const { data, error } = await supabase
      .from('daily_tracking')
      .upsert(dbData, { onConflict: 'id' })
      .select()
      .single();

    if (error) {
      let msg = 'Nie udało się zapisać danych.';
      if (error.code === '23505') msg = 'Ten wpis już istnieje. Odśwież stronę.';
      else if (error.code === '23514') msg = 'Nieprawidłowa wartość danych.';
      else if (error.message?.includes('JWT')) msg = 'Sesja wygasła. Zaloguj się ponownie.';
      else if (error.message) msg = error.message;
      throw new Error(msg);
    }

    return fromDbFormat(data);
  },

  async getRange(_userId: string, startDate: string, endDate: string): Promise<DailyTracking[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Użytkownik nie jest zalogowany');
    if (!startDate || !endDate || !isIsoDate(startDate) || !isIsoDate(endDate)) {
      throw new Error('Nieprawidłowy zakres dat (wymagany format YYYY-MM-DD)');
    }

    const { data, error } = await supabase
      .from('daily_tracking')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: false });

    if (error) throw error;
    return (data || []).map(fromDbFormat);
  },

  async delete(id: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Użytkownik nie jest zalogowany');
    const { error } = await supabase
      .from('daily_tracking')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);
    if (error) throw error;
  }
};
